// @flow

import * as React from 'react';
import {useEffect, useState} from 'react';
import Box from 'sail/Box';
import Group from 'sail/Group';
import {Subheading, Text} from 'sail/Text';
import {UnstyledLink} from 'sail/UnstyledLink';
import {useAccount, useLocation, useVideos} from 'docs/data';
import {useActivePage} from 'docs/hooks';
import {getScrolledPastSections} from 'docs/util';
import './TableOfContents.scss';
import {defineMessages, FormattedMessage} from 'react-intl';
import type {Article, ArticlePage, ArticleSection} from 'docs/data';
import {VideoPlayerPlaceholder} from 'docs/shell/videos/VideoPlayerPlaceholder';
import {
  ROUTE_TO_EXPERIMENT_GROUP,
  RecommendedDocs,
} from '../experiments/RecommendedDocs';

const messages = defineMessages({
  tocHeader: {
    id: 'docsShell.navigation.toc',
    description: 'This is the header for a table of contents section',
    defaultMessage: 'On this page',
  },
});

const getActiveSection = (
  page: ArticlePage,
  containerEl: HTMLElement,
): ArticleSection => {
  const {sections} = page;
  const containerHeight = containerEl.getBoundingClientRect().height;

  // If the container is scrolled to the bottom, ensure the last section
  // is the active focus. (This covers the case where the last section isn't
  // tall enough to be able to scroll high enough to capture the focus.)
  if (containerEl.scrollHeight - containerEl.scrollTop <= containerHeight) {
    return sections[sections.length - 1];
  }

  const scrolledPastSections = getScrolledPastSections(
    sections,
    containerHeight,
  );

  // The last section whose heading element has been scrolled past is considered active.
  return scrolledPastSections[scrolledPastSections.length - 1];
};

type TableOfContentsHeadingProps = {|
  section: ArticleSection,
  isActive: boolean,
|};

export const TableOfContentsHeading = ({
  section,
  isActive,
}: TableOfContentsHeadingProps) => {
  const {pushLocation} = useLocation();
  const activePage = useActivePage();
  const hash = `#${section.id}`;

  const handleClick = (event: SyntheticEvent<HTMLAnchorElement>) => {
    const el = document.getElementById(section.id);

    if (!el) {
      return;
    }

    // If the section anchor is within an expandable element, ensure that it's expanded.
    const expandableEl = el.closest('[aria-expanded]');
    if (expandableEl) {
      expandableEl.setAttribute('aria-expanded', 'true');
    }

    // Append query params to the URL when selecting a TOC link
    // A page with platform "main" means we only have one page on the article, so skip appending
    const query = activePage.prefs.platform === 'main' ? {} : activePage.prefs;

    pushLocation({hash, query});
    el.focus();

    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <Text color={isActive ? 'blue' : 'gray'} lineHeight={16}>
      <UnstyledLink href={hash} title={section.title} onClick={handleClick}>
        {section.title}
      </UnstyledLink>
    </Text>
  );
};

export type TableOfContentsProps = {|
  article: Article,
  contentRef: React.ElementRef<any>,
|};

export const TableOfContents = ({
  article,
  contentRef,
}: TableOfContentsProps) => {
  const {account} = useAccount();
  const {location} = useLocation();
  const {tocVideo} = useVideos();
  const activePage = useActivePage();
  const [activeSection, setActiveSection] = useState(activePage.sections[0]);

  // Whenever the active page changes, or the user scrolls the content frame,
  // keep the active section up-to-date.
  useEffect(() => {
    const content = contentRef?.current;

    const updateActiveSection = () => {
      setActiveSection(getActiveSection(activePage, contentRef.current));
    };

    if (content) {
      updateActiveSection();
      content.addEventListener('scroll', updateActiveSection);
      return () => {
        content.removeEventListener('scroll', updateActiveSection);
      };
    }
  }, [contentRef, article, activePage]);

  const isInSidebarContentExperiment =
    account.treatments['docs_sidebar_content'] === 'treatment';
  const isExperimentalRoute = !!ROUTE_TO_EXPERIMENT_GROUP[location.pathname];

  const showToC = activePage.sections.length >= 2 && article.features.toc;
  const showRecDocs = isExperimentalRoute && isInSidebarContentExperiment;

  // Don't show a TOC if it's disabled. If there aren't enough sections in
  // the active page shows recommended docs
  let tableOfContents;

  if (!showToC && showRecDocs) {
    tableOfContents = (
      <Box className="TableOfContents">
        <RecommendedDocs />
      </Box>
    );
  } else if (!showToC && !showRecDocs) {
    tableOfContents = null;
  } else {
    tableOfContents = (
      <Box className="TableOfContents">
        {tocVideo && <VideoPlayerPlaceholder />}
        <Subheading className="TableOfContents-header">
          <FormattedMessage {...messages.tocHeader} />
        </Subheading>
        <Group spacing={12}>
          {activePage.sections.map((section) => (
            <TableOfContentsHeading
              key={section.id}
              section={section}
              isActive={section === activeSection}
            />
          ))}
        </Group>
        {showRecDocs && <RecommendedDocs />}
      </Box>
    );
  }
  return tableOfContents;
};
